import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { useSearchParams } from "react-router-dom";
import { db } from "@/firebase/firebase";
import { useAuth } from "@/context/AuthContext";
import { format, isThisMonth, isWithinInterval, subDays } from "date-fns";

export default function PrintNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [searchParams] = useSearchParams();

  const typeFilter = searchParams.get("type") || "all";
  const range = searchParams.get("range") || "all";

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "notifications"),
      where("userId", "==", user.uid),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((n) => n.title && n.timestamp);

      setNotifications(data);
    });

    return unsubscribe;
  }, [user]);

  const applyFilters = () => {
    return notifications
      .filter((n) => {
        if (typeFilter !== "all" && n.type !== typeFilter) return false;

        const date = n.timestamp?.toDate?.();
        if (!date) return false;

        if (range === "week") {
          return isWithinInterval(date, {
            start: subDays(new Date(), 7),
            end: new Date(),
          });
        } else if (range === "month") {
          return isThisMonth(date);
        }

        return true;
      })
      .sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());
  };

  const grouped = applyFilters().reduce((acc, n) => {
    const day = format(n.timestamp.toDate(), "yyyy-MM-dd");
    if (!acc[day]) acc[day] = [];
    acc[day].push(n);
    return acc;
  }, {});

  useEffect(() => {
    setTimeout(() => window.print(), 500); // auto trigger print
  }, []);

  return (
    <div className="p-6 print:p-0 text-black print:text-black font-sans">
      <h1 className="text-2xl font-bold mb-4 print:text-xl print:mb-2">
        Notifications Report
      </h1>
      <p className="mb-4 text-sm print:text-xs">
        Filter: {typeFilter} | Date Range: {range}
      </p>

      {Object.keys(grouped).length === 0 ? (
        <p className="text-zinc-500 text-sm">No matching notifications found.</p>
      ) : (
        Object.entries(grouped).map(([date, items]) => (
          <div key={date} className="mb-6 break-inside-avoid">
            <h2 className="text-sm font-semibold text-zinc-600 mb-1">
              {format(new Date(date), "eeee, MMMM do, yyyy")}
            </h2>
            <table className="w-full text-sm border border-collapse print:text-xs">
              <thead>
                <tr className="border-b bg-zinc-100">
                  <th className="text-left p-2">Title</th>
                  <th className="text-left p-2">Body</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Read</th>
                  <th className="text-left p-2">Time</th>
                </tr>
              </thead>
              <tbody>
                {items.map((n) => (
                  <tr key={n.id} className="border-b">
                    <td className="p-2">{n.title}</td>
                    <td className="p-2">{n.body}</td>
                    <td className="p-2 capitalize">{n.type}</td>
                    <td className="p-2">{n.read ? "✔" : ""}</td>
                    <td className="p-2">
                      {format(n.timestamp.toDate(), "hh:mm a")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}
